import { useEffect, useSyncExternalStore } from "react";

/**
 * Registre du « bandeau » de page (header dégradé bleu marine du DA).
 *
 * Le prototype DA n'a QU'UN header par page : le bandeau `h-14` bleu, avec le
 * logo CATHO+ blanc calé à droite. Notre app monte en plus une barre d'outils
 * globale (`AppShellHeader`, toggle dark/light) en `fixed top-2 right-4`, qui
 * se superposait pile sur ce logo.
 *
 * Solution : les composants qui rendent un bandeau (`PageHeader`,
 * `DashboardHeader`) s'enregistrent ici et affichent eux-mêmes le toggle,
 * intégré à gauche du logo. `AppShellHeader` s'efface tant qu'un bandeau est
 * monté et ne sert plus que de repli pour les pages sans bandeau.
 *
 * Un seul `DarkModeToggle` est donc rendu à la fois — deux instances auraient
 * des états locaux désynchronisés (chacune lit `localStorage` à l'init).
 */
let count = 0;
const listeners = new Set<() => void>();

function emit() {
  listeners.forEach((l) => l());
}

function subscribe(callback: () => void) {
  listeners.add(callback);
  return () => {
    listeners.delete(callback);
  };
}

function getSnapshot() {
  return count > 0;
}

/** À appeler dans un composant qui rend le bandeau de page (effet de montage). */
export function usePageBand() {
  useEffect(() => {
    count += 1;
    emit();
    return () => {
      count -= 1;
      emit();
    };
  }, []);
}

/** `true` dès qu'au moins un bandeau de page est monté. */
export function usePageBandMounted() {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

/** Réinitialise le registre — usage tests uniquement. */
export function resetPageBandRegistry() {
  count = 0;
  emit();
}
